  // Service Types

  // API Response interfaces
  export interface LoginResponse {
    success: boolean;
    userId?: number;
    message?: string;
    requiresSecondFactor?: boolean;
  }

  // Second Factor Response
  export interface SecondFactorResponse {
    success: boolean;
    message?: string;
    codeSent?: boolean;
  }

  // Final Login Response
  export interface FinalLoginResponse {
    success: boolean;
    token?: string;
    user?: {
      id: number;
      name: string;
      email: string;
    };
    message?: string;
  }

  // Forget Userkey Response
  export interface ForgetUserkeyResponse {
    success: boolean;
    message?: string;
  }

  /** Body POST /v1/bff/gibs/recuperar-senha */
  export interface ForgetUserkeyRequest {
    cpf: string;
    email: string;
    aplicacao: string;
  }

  /** Body POST /v1/bff/gibs/recuperar-usuario */
  export interface RecoverUserRequest {
    cpf: string;
  }

  /** Response POST /v1/bff/gibs/recuperar-usuario */
  export interface RecoverUserResponse {
    nome: string;
    dataNascimento: string;
    cpf: string;
    cn: string;
    email: string;
  }

  // Login Service (CADP)
  export interface ILoginService {
    verifyUser: (login: string, senha: string) => Promise<import('./apiType').VerifyUserResponse>;
    getContacts: (idPessoa: string) => Promise<import('./apiType').GetContactsResponse>;
    sendSms: (to: string, msg: string) => Promise<void>;
    sendEmail: (request: import('./apiType').SendEmailRequest) => Promise<void>;
    loginFinal: (cpf: string) => Promise<import('./apiType').LoginFinalResponse>;
    postListBusinessLinksByPessoaId: (idPessoa: string) => Promise<import('./apiType').ListBusinessLinksResponse>;
  }

  // Userkey Maintenance Service
  export interface IUserkeyMaintenanceService {
    postRecoverUser: (request: RecoverUserRequest) => Promise<RecoverUserResponse>;
    postForgetUserkey: (request: ForgetUserkeyRequest) => Promise<ForgetUserkeyResponse>;
  }

  // Auth Service
  export interface IAuthService {
    postLogout: () => Promise<LogoutResponse>;
    getTokenSession: () => Promise<TokenSessionResponse>;
  }

  // Policyholder Service (CADP)
  export interface IRegisterPolicyholderService {
    postAddBasicPhysicalPersonData: (data: AddBasicPhysicalPersonDataRequest) => Promise<AddBasicPhysicalPersonDataResponse>;
    postGetLegalEntityByCnpj: (cnpj: string) => Promise<GetLegalEntityByCnpjResponse>;
    postGetPhysicalPersonByCpf: (cpf: string) => Promise<GetPhysicalPersonByCpfResponse>;
    postIncluirRelacionamentoNegocio: (data: IncluirRelacionamentoNegocioRequest) => Promise<IncluirRelacionamentoNegocioResponse>;
    postLinkPersonToBusinessRepresentation: (data: LinkPersonToBusinessRepresentationRequest) => Promise<LinkPersonToBusinessRepresentationResponse>;
    /** filtered links; `produtoIds` 3=Saúde, 4=Vida, 5=Previdência */
    postListFilteredBusinessLinks: (params: ListFilteredBusinessLinksParams) => Promise<ListBusinessLinkFiltersResponse>;
    /** POST /cadp/adicionar-email — use `ORIGEM_DADO_ADICIONAR_EMAIL` and `TipoPessoaCadp` */
    postAddEmail: (data: AddEmailRequest) => Promise<AddEmailResponse>;
    /** POST /gibs/alterar-senha */
    postAlterPassword: (data: AlterPasswordRequest) => Promise<AlterPasswordResponse>;
    postContactLookup: (data: ContactLookupRequest) => Promise<import('./apiType').GetContactsResponse>;
    getUserPoliciesList: (params: UserPoliciesListParams) => Promise<UserPoliciesListResponse>;
  }

  // Auth Service
  export interface LogoutResponse {
    success: boolean;
    message?: string;
  }

  export interface RefreshTokenResponse {
    success: boolean;
    token?: string;
    message?: string;
  }

  export interface TokenSessionResponse {
    success?: boolean;
    token?: string;
    idPessoa?: number;
    cpf?: string;
    pessoaFisicaResponse?: {
      base: string;
      mensagem: string | null;
      pessoaFisica: {
        id: string;
        nome: string;
        cpf: string;
        dataNascimento: string;
        nomeMae: string;
        sexo: string;
        estadoCivilId: string;
        grauEscolaridadeId: string;
      };
    };
    message?: string;
  }

  // Policyholder Service
  export interface CpfData {
    idPessoa?: string;
    /** CADP PF version (`recuperar-pessoa-fisica.versao` or `incluir-dados-basicos.numeroVersao`) */
    versao?: string;
    cpf: string;
    name: string;
    email: string;
    corporateEmail?: string;
      /** true quando `pickFirstNonPersonalEmail` find domain corporate in contacts (field blocked in form) */
    hasCorporateEmailFromContacts?: boolean;
    phone: string;
    birthDate?: string;
    biologicalSex?: string;
    nomeMae?: string;
    nomePai?: string;
    estadoCivilId?: string;
    grauEscolaridadeId?: string;
    position?: string;
    rg?: string;
    issuingAgency?: string;
    issueDate?: string;
  }

  // CNPJ data for policyholder (id = CADP pessoaJuridica.id)
  export interface CnpjData {
    id?: string;
    /** Legal entity version (maps to numeroVersaoPessoaPrimario on link) */
    versao?: string;
    cnpj: string;
    corporateName: string;
    fantasyName?: string;
    openingDate?: string;
    email?: string;
    phone?: string;
  }

  /** Body POST /cadp/consultar-contato */
  export interface ContactLookupRequest {
    cpf: string;
    idPessoa: string;
    cnpj: string;
  }

  /** CADP recuperar-pessoa-fisica (BFF CadpRecuperarPessoaFisicaResponseDto shape) */
  export interface CadpPessoaFisicaDto {
    id: string;
    versao?: string;
    nome: string;
    cpf: string;
    dataNascimento?: string;
    nomeMae?: string;
    sexo?: string;
    estadoCivilId?: string;
    grauEscolaridadeId?: string;
  }

  // Get physical person by CPF (POST /cadp/recuperar-pessoa-fisica?cpf=)
  export interface GetPhysicalPersonByCpfResponse {
    /** BFF UserBffLoginResponse (wrapper around CADP payload) */
    idPessoa?: number;
    cpf?: string;
    pessoaFisicaResponse?: {
      base?: string;
      mensagem?: string | null;
      pessoaFisica?: CadpPessoaFisicaDto | null;
    };
    /** Core/CADP shape (direct, without BFF wrapper) */
    base?: string;
    mensagem?: string | null;
    pessoaFisica?: CadpPessoaFisicaDto | null;
  }

  /** GET /v1/bff/vida/apolice/listar-apol-perf-usuario */
  export interface UserPoliciesListParams {
    numApoliceApol?: number;
    /** CNPJ digits only (query) */
    numDocumentoApol?: string;
  }

  /**
   * One policy row inside API `retorno` (step 5 in product flow spec).
   */
  export interface UserPoliciesListItem {
    numDocumentoApol?: string;
    codEmpresaApol?: number;
    codSucursalApol?: number;
    codRamoApol?: number;
    codprodutoApol?: number;
    numApoliceApol?: number;
    identContratoApol?: number;
    nomeEmpresaApol?: string;
    nomeSurcusalApol?: string;
    nomeRamoApol?: string;
    nomeProdutoApol?: string;
    identEstipulanteApol?: number;
    cdSubEstipulanteApol?: number;
    nomeEstipulanteApol?: string;
    dataEmissao?: string;
    dataInicioVigencia?: string;
    dataFimVigencia?: string;
    tipoDocumentoApol?: number;
    nomeSucursalNegocio?: string;
    codSucursalNegocio?: number;
    identContrato?: number | null;
  }

  /**
   * API block: policy list + status message.
   */
  export interface UserPoliciesListGroup {
    retorno?: UserPoliciesListItem[] | null;
    mensagem?: string | null;
  }

  /**
   * Step 5 response: **array** of blocks (may be a single element).
   * @example
   * ```json
   * [
   *   {
   *     "retorno": [ { "numDocumentoApol": "...", "codEmpresaApol": 686, ... } ],
   *     "mensagem": "Consulta efetuada com sucesso!"
   *   }
   * ]
   * ```
   */
  export type UserPoliciesListResponse = UserPoliciesListGroup[];

  /** @deprecated Use {@link UserPoliciesListItem} */
  export type ListarApolPerfUsuarioApolice = UserPoliciesListItem;
  /** @deprecated Use {@link UserPoliciesListParams} */
  export type ListarApolPerfUsuarioParams = UserPoliciesListParams;
  /** @deprecated Use {@link UserPoliciesListGroup} */
  export type ListarApolPerfUsuarioGrupo = UserPoliciesListGroup;
  /** @deprecated Use {@link UserPoliciesListResponse} */
  export type ListarApolPerfUsuarioResponse = UserPoliciesListResponse;
  /** @deprecated Use {@link UserPoliciesListItem} */
  export type ListarApolPerfUsuarioRetorno = UserPoliciesListItem;
  /** @deprecated Use {@link ContactLookupRequest} */
  export type ConsultarContatoRequest = ContactLookupRequest;

  // Get legal entity by CNPJ (POST /cadp/recuperar-pessoa-juridica?cnpj=)
  export interface PersonRole {
    id: string;
    descricao: string;
    descricaoReduzida?: string;
  }

  export interface DataOrigin {
    id: string;
    descricao: string;
    idSegmentoNegocio?: string;
  }

  export interface LegalEntity {
    id: string;
    nome: string;
    versao: string;
    versaoChancelada: boolean;
    tipoPessoa: string;
    papeis: PersonRole[];
    origemDado: DataOrigin;
    nomeFantasia?: string;
    isencaoInscricaoEstadual?: boolean;
    grupoBradesco?: boolean;
    comercializacaoProduto?: boolean;
    dataAberturaJuntaComercial?: string;
    descricaoNatureza?: string;
    cnpj: string;
  }

  export interface GetLegalEntityByCnpjResponse {
    base?: string;
    mensagem?: string | null;
    pessoaJuridica?: LegalEntity | null;
  }

  // Add basic physical person data (POST /cadp/incluir-dados-basicos-pessoa-fisica)
  export interface AddBasicPhysicalPersonDataRequest {
    nome: string;
    telefone: string;
    nomeDaMae: string;
    dataNascimento: string;
    cpf: string;
    email: string;
    pass: string;
    userName: string;
  }

  export interface PersonIdentifier {
    idPessoa: string;
    numeroVersao: string;
  }

  export interface AddBasicPhysicalPersonDataResponse {
    identificadorPessoa: PersonIdentifier;
  }

  /** POST /cadp/incluir-relacionamento-negocio — cria representação de negócio (passo 6) */
  export interface IncluirRelacionamentoNegocioRequest {
    id: number;
    versao: number;
    chaveLegado: string;
    papelId: string;
    origemDadoId: string;
    dataInicioVigencia: string;
    dataFimVigencia: string;
  }

  export interface IncluirRelacionamentoNegocioResponse {
    id?: string;
    idRepresentacaoNegocio?: string;
  }

  // Link person to business representation (POST /cadp/vincular-pessoa-representacao-negocio)
  export interface LinkPersonToBusinessRepresentationRequest {
    idPessoaPrimario: number;
    numeroVersaoPessoaPrimario: number;
    idRepresentacaoNegocio: number;
    idPessoaSecundario: number;
    numeroVersaoPessoaSecundario: number;
    papelPessoaSecundario: string;
    responsavelInclusao: number;
  }

  /** Query params for POST /cadp/listar-vinculo-negocio/filtrados */
  export interface ListFilteredBusinessLinksParams {
    idPessoa: number;
    /** Single product filter: 3=Saúde, 4=Vida, 5=Previdência */
    produtoIds?: number;
    /** Filter ESTP.cnpj.contrato.ramo — after choosing a policy, refine links by contract */
    chaveLegado?: string;
  }

  export interface LinkPersonToBusinessRepresentationResponse {
    idRepresentacaoNegocio: string;
    idVinculoNegocio: string;
  }

  // List business link filters (POST /cadp/listar-vinculo-negocio/filtrados)
  export interface BusinessLinkSegment {
    id: string;
    descricao: string;
    tipoSegmento: string;
  }

  export interface BusinessLinkRepresentation {
    id: string;
    chaveLegado: string;
    segmento: BusinessLinkSegment;
    papel: { id: string; descricao: string };
    dataInicioVigencia: string;
    situacaoNegocio: { id: string; descricao: string };
    dataInclusao: string;
  }

  export interface BusinessLinkItem {
    idVinculoNegocio: string;
    segmento: BusinessLinkSegment;
    representacao: BusinessLinkRepresentation;
  }

  export interface ListBusinessLinkFiltersResponse {
    vinculos: BusinessLinkItem[];
  }

  /** CADP: physical person = 3, legal entity = 4 (ex.: add email) */
  export const TipoPessoaCadp = {
    Fisica: 3,
    Juridica: 4,
  } as const;

  export type TipoPessoaCadp = (typeof TipoPessoaCadp)[keyof typeof TipoPessoaCadp];

  /** Fixed origin required by CADP to add email */
  export const ORIGEM_DADO_ADICIONAR_EMAIL = 122 as const;

  /** Body POST /cadp/adicionar-email (aligned with CadpAdicionarEmailRequest in BFF) */
  export interface AddEmailRequest {
    idPessoa: string;
    tipoPessoa: TipoPessoaCadp;
    origemDado: typeof ORIGEM_DADO_ADICIONAR_EMAIL;
    enderecoEletronico: string;
  }

  export type AddEmailResponse = void;

  /** Body POST /gibs/alterar-senha (AlterarSenhaGibsRequest no BFF) */
  export interface AlterPasswordRequest {
    cpf: string;
    senhaAtual: string;
    novaSenha: string;
  }

  export type AlterPasswordResponse = void;

  export type PolicyholderSearchCnpjResponse = GetLegalEntityByCnpjResponse;
  export type PolicyholderSearchCpfResponse = GetPhysicalPersonByCpfResponse;
  export type PolicyholderRegisterRequest = AddBasicPhysicalPersonDataRequest;

  // Business links (legacy structure for listing)
  export interface PolicyholderBusinessLink {
    id: string;
    number: string;
    description: string;
    origin: string;
  }

  export type PolicyholderBusinessLinksResponse = ListBusinessLinkFiltersResponse;

  // Policyholder CPF Roles Response
  export interface PolicyholderCpfRolesResponse {
    success: boolean;
    message?: string;
    data?: PolicyholderBusinessRole[];
    totalElements?: number;
  }

  // Policyholder Business Role
  export interface PolicyholderBusinessRole {
    id: string;
    description: string;
    origin: string;
  }

  // Policyholder CPF Role
  export interface PolicyholderCpfRole {
    id: string;
    description: string;
  }

  export interface EntraLoginRequest {
    code: string;
    codeVerifier: string;
    nonce: string;
  }

  // Public BFF body for Entra ID backoffice (no CADP id/CPF).
  export interface EntraLoginResponse {
    role?: string;
    chaveM?: string;
    email?: string;
    /** Legacy identifier; current BFF may still send UPN/email here. */
    cpf?: string;
  }

  export interface IEntraIdAuthService {
    postEntraLogin(request: EntraLoginRequest): Promise<EntraLoginResponse>;
  }